import { Layout, Textbox } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";

/**
 * Utility class for validating field indices in mobile communication control layouts.
 * Provides common validation logic used across all child control layouts.
 */
export class MobileCommFieldValidator {
    /**
     * Validates all field index textboxes to ensure values are within valid range.
     * @param layout - The layout containing the textboxes to validate
     * @param textboxIds - Array of textbox IDs to validate
     * @param getMaxRowIndex - Function to get the maximum row index
     * @returns true if all validations pass, false otherwise
     */
    public static validateAllFieldIndices(
        layout: Layout,
        textboxIds: string[],
        getMaxRowIndex: () => number
    ): boolean {
        const maxRowIndex = getMaxRowIndex();

        for (const textboxId of textboxIds) {
            const textbox = layout.findComponentById(textboxId) as Textbox;
            if (!textbox || !textbox.text) {
                continue;
            }

            const fieldValue = MobileCommFieldValidator.parseFieldValue(textbox.text);
            if (!MobileCommFieldValidator.isValidFieldIndex(fieldValue, maxRowIndex)) {
                MobileCommFieldValidator.showValidationError(textbox, maxRowIndex);
                return false;
            }
        }

        return true;
    }

    /**
     * Gets the maximum valid row index from the parent table.
     * @param ownerLayout - The parent layout containing the form field table
     * @returns maximum row index (0-based)
     */
    public static getMaxRowIndex(ownerLayout: any): number {
        return (ownerLayout?.tableFormFieldOpt?.rows?.length ?? 0) - 1;
    }

    /**
     * Parses a text value to a number.
     * @param text - the text to parse
     * @returns parsed number value
     */
    public static parseFieldValue(text: string): number {
        return Number(text);
    }

    /**
     * Validates if a field index value is within the acceptable range.
     * @param value - the field index value to validate
     * @param maxIndex - the maximum allowed index
     * @returns true if valid, false otherwise
     */
    public static isValidFieldIndex(value: number, maxIndex: number): boolean {
        return !isNaN(value) && value >= -1 && value <= maxIndex;
    }

    /**
     * Shows a validation error dialog and focuses the invalid textbox.
     * @param textbox - the textbox with invalid value
     * @param maxIndex - the maximum allowed index
     */
    public static showValidationError(textbox: Textbox, maxIndex: number): void {
        textbox.focus();
        if (maxIndex === -1) {
            CommonDialogs.showError(
                `The value in ${textbox.caption} cannot be set. No fields have been configured for this form.`
            );
        } else {
            CommonDialogs.showError(
                `The value in ${textbox.caption} is invalid. Please enter a value between 0 and ${maxIndex}.`
            );
        }
    }

    /**
     * Validates a field index on blur event and updates textbox validation warning.
     * Also checks the linked checkboxes if validation passes.
     * When Date and Time fields are adjacent in the textboxGroup, both are required if checkbox is checked.
     * @param textbox - the textbox that triggered the blur event
     * @param linkedCheckbox - the checkbox or array of checkboxes associated with this field (optional)
     * @param textboxGroup - the textbox or array of textboxes in the group (optional)
     * @param getMaxRowIndex - function to get the maximum row index
     */
    public static validateFieldOnBlur(
        textbox: Textbox,
        linkedCheckbox: any | any[] | null,
        textboxGroup: any | any[] | null,
        getMaxRowIndex: () => number
    ): void {
        const checkboxes = MobileCommFieldValidator.normalizeToArray(linkedCheckbox);
        const textboxes = MobileCommFieldValidator.normalizeToArray(textboxGroup);
        let{ hasDateTimeGroup, dateField, timeField } = MobileCommFieldValidator.detectDateTimeGroup(textboxes);

        if (textbox.id === "textFieldAvailableDate" || textbox.id === "textFieldAvailableTime") {
            hasDateTimeGroup = true;
            dateField = textboxes.find(tb => tb.id === "textFieldAvailableDate");
            timeField = textboxes.find(tb => tb.id === "textFieldAvailableTime");
        }

        // Handle empty textbox without date/time group
        if (!textbox.text && !hasDateTimeGroup) {
            MobileCommFieldValidator.handleEmptyField(textbox, textboxes, checkboxes);
            return;
        }

        // Validate field index range
        const fieldValue = MobileCommFieldValidator.parseFieldValue(textbox.text);
        const maxRowIndex = getMaxRowIndex();

        if (!MobileCommFieldValidator.isValidFieldIndex(fieldValue, maxRowIndex)) {
            MobileCommFieldValidator.setFieldIndexValidationWarning(textbox, fieldValue, maxRowIndex);
            return;
        }

        // Field is valid - clear warnings and handle date/time validation
        textbox.validationWarning = null;

        if (hasDateTimeGroup) {
            MobileCommFieldValidator.validateDateTimeGroup(dateField, timeField, checkboxes);
        } else {
            MobileCommFieldValidator.checkAllCheckboxes(checkboxes);
        }
    }

    /**
     * Normalizes a single item or array to an array.
     * @param item - single item, array, or null
     * @returns array of items
     */
    private static normalizeToArray(item: any | any[] | null): any[] {
        if (Array.isArray(item)) {
            return item;
        }
        return item ? [item] : [];
    }

    /**
     * Handles validation when textbox is empty.
     * @param textbox - the empty textbox
     * @param textboxes - related textboxes in the group
     * @param checkboxes - linked checkboxes
     */
    private static handleEmptyField(textbox: Textbox, textboxes: any[], checkboxes: any[]): void {
        textbox.validationWarning = null;

        if (textboxes.every(tb => !tb.text)) {
            MobileCommFieldValidator.uncheckAllCheckboxes(checkboxes);
        }
    }

    /**
     * Sets the validation warning message for invalid field index.
     * @param textbox - the textbox with invalid value
     * @param fieldValue - the invalid field value
     * @param maxRowIndex - the maximum allowed index
     */
    private static setFieldIndexValidationWarning(
        textbox: Textbox,
        fieldValue: number,
        maxRowIndex: number
    ): void {
        if (maxRowIndex === -1) {
            textbox.validationWarning = "No fields have been configured for this form.";
        } else {
            textbox.validationWarning =
                `${fieldValue} is not a valid option. Please enter a value between 0 and ${maxRowIndex}.`;
        }
    }

    /**
     * Validates date and time field group requirements.
     * @param dateField - the date field textbox
     * @param timeField - the time field textbox
     * @param checkboxes - linked checkboxes
     */
    private static validateDateTimeGroup(dateField: any, timeField: any, checkboxes: any[]): void {
        const anyCheckboxChecked = MobileCommFieldValidator.isAnyCheckboxChecked(checkboxes);
        const bothPopulated = dateField?.text && timeField?.text;
        const neitherPopulated = !dateField?.text && !timeField?.text;

        if (!anyCheckboxChecked && (bothPopulated || neitherPopulated)) {
            MobileCommFieldValidator.clearDateTimeValidation(dateField, timeField);
            return;
        }

        MobileCommFieldValidator.setDateTimeRequiredWarnings(dateField, timeField);

        if (!neitherPopulated) {
            MobileCommFieldValidator.checkAllCheckboxes(checkboxes);
        } else {
            MobileCommFieldValidator.uncheckAllCheckboxes(checkboxes);
            MobileCommFieldValidator.clearDateTimeValidation(dateField, timeField);
        }
    }

    /**
     * Checks if any checkbox in the array is checked.
     * @param checkboxes - array of checkboxes
     * @returns true if any checkbox is checked
     */
    private static isAnyCheckboxChecked(checkboxes: any[]): boolean {
        return checkboxes.some(checkbox => checkbox.checked === true);
    }

    /**
     * Clears validation warnings and required flags for date/time fields.
     * @param dateField - the date field textbox
     * @param timeField - the time field textbox
     */
    private static clearDateTimeValidation(dateField: any, timeField: any): void {
        dateField.validationWarning = null;
        timeField.validationWarning = null;
        dateField.required = false;
        timeField.required = false;
    }

    /**
     * Sets required warnings for date/time fields based on which are populated.
     * @param dateField - the date field textbox
     * @param timeField - the time field textbox
     */
    private static setDateTimeRequiredWarnings(dateField: any, timeField: any): void {
        const datePopulated = !!dateField?.text;
        const timePopulated = !!timeField?.text;
        const message = "Both Date and Time are required.";

        if (!datePopulated && !timePopulated) {
            dateField.required = true;
            dateField.validationWarning = message;
            timeField.required = true;
            timeField.validationWarning = message;
        } else if (datePopulated && !timePopulated) {
            timeField.required = true;
            timeField.validationWarning = message;
        } else if (timePopulated && !datePopulated) {
            dateField.required = true;
            dateField.validationWarning = message;
        }
    }

    /**
     * Checks all checkboxes in the array.
     * @param checkboxes - array of checkboxes to check
     */
    private static checkAllCheckboxes(checkboxes: any[]): void {
        for (const checkbox of checkboxes) {
            checkbox.checked = true;
        }
    }

    /**
     * Unchecks all checkboxes in the array.
     * @param checkboxes - array of checkboxes to uncheck
     */
    private static uncheckAllCheckboxes(checkboxes: any[]): void {
        for (const checkbox of checkboxes) {
            checkbox.checked = false;
        }
    }

    /**
     * Detects if textboxes contain adjacent Date and Time fields.
     * @param textboxes - array of textboxes to check
     * @returns object containing detection result and field references
     */
    private static detectDateTimeGroup(textboxes: any[]): {
        hasDateTimeGroup: boolean;
        dateField: any | null;
        timeField: any | null;
    } {
        if (textboxes.length < 2) {
            return { hasDateTimeGroup: false, dateField: null, timeField: null };
        }

        // Look for adjacent Date and Time fields
        for (let i = 0; i < textboxes.length - 1; i++) {
            const current = textboxes[i];
            const next = textboxes[i + 1];

            const currentCaption = current?.caption?.toLowerCase() || '';
            const nextCaption = next?.caption?.toLowerCase() || '';

            // Check if current is Date and next is Time, or vice versa
            if (currentCaption.includes('date') && nextCaption.includes('time')) {
                return { hasDateTimeGroup: true, dateField: current, timeField: next };
            }
        }

        return { hasDateTimeGroup: false, dateField: null, timeField: null };
    }

    /**
     * Handles checkbox change events for date/time field pairs.
     * Sets validation warnings and required flags based on checkbox state and field values.
     * @param checkbox - the checkbox that triggered the event
     * @param dateField - the date textbox field
     * @param timeField - the time textbox field
     * @param validationMessage - optional custom validation message (defaults to "Both Date and Time are required.")
     */
    public static handleDateTimeCheckboxUpdate(
        checkbox: any,
        dateField: any,
        timeField: any,
        validationMessage: string = "Both Date and Time are required."
    ): void {
        if (!checkbox.checked) {
            dateField.validationWarning = null;
            timeField.validationWarning = null;
            dateField.required = false;
            timeField.required = false;
        } else {
            if (!dateField?.text && !timeField?.text) {
                dateField.required = true;
                dateField.validationWarning = validationMessage;
                timeField.required = true;
                timeField.validationWarning = validationMessage;
            } else if (dateField?.text && !timeField?.text) {
                timeField.required = true;
                timeField.validationWarning = validationMessage;
            } else if (timeField?.text && !dateField?.text) {
                dateField.required = true;
                dateField.validationWarning = validationMessage;
            }
        }
    }
}
